import { useQuery } from "@apollo/client";
import {
  Box,
  Divider,
  InputAdornment,
  MenuItem,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { ReactElement } from "react";

import {
  GetTenantPriceDataQuery,
  GetTenantPriceDataQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import CollapsibleDrawer from "~frontend/components/CollapsibleDrawer";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink } from "~frontend/components/Link";
import { FormikSelect, FormikTextField } from "~frontend/components/forms";
import { PRICE_DATA_QUERY } from "~frontend/graphql/priceOverrides";
import { palette } from "~frontend/theme";

import { formatMillicentsAsDollars } from "../pricing/priceFormatting";

const InputRow = ({
  children,
}: {
  children: ReactElement | (string | ReactElement)[];
}): ReactElement => (
  <FlexBox
    sx={{
      "> .MuiFormControl-root": { minWidth: "48%", flexGrow: 1 },
      columnGap: 4,
    }}
  >
    {children}
  </FlexBox>
);

export function BudgetCalculator({
  sx,
}: {
  sx?: SxProps<Theme>;
}): ReactElement {
  const {
    data: priceData,
    loading: priceDataLoading,
    error: priceDataError,
  } = useQuery<GetTenantPriceDataQuery, GetTenantPriceDataQueryVariables>(
    PRICE_DATA_QUERY
  );

  return (
    <Box sx={sx}>
      <CollapsibleDrawer
        title={
          <Typography variant="body2Bold" sx={{ color: palette.navy }}>
            Budget Calculator
          </Typography>
        }
        startOpen={false}
        variant="slim"
        sxHeader={{
          backgroundColor: palette.midGray,
          py: 0.5,
          px: 2,
        }}
      >
        <Box
          sx={{
            background: palette.lightestGray,
            p: "16px",
          }}
        >
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            The Budget Calculator is intended to help you calculate a budget
            based on message estimates. Actual cost of messages will depend on a
            number of factors. In an SMS message, the first segment can hold 160
            characters. For SMS longer than one segment, each can hold up to 153
            characters. Emojis, accented letters, and non-English characters
            take up more space. If your message goes over 3 segments, it will be
            sent as an MMS at a flat rate.{" "}
            <ExternalLink href="https://scaletowin.freshdesk.com/support/solutions/articles/66000041193-using-the-segment-estimator-character-counter">
              Learn more about segments.
            </ExternalLink>
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            You won't be charged for incoming responses, but reply rate can be
            used to estimate the number of replies you will want to send.
          </Typography>
          <AsyncWrapper
            data={priceData}
            loading={priceDataLoading}
            errors={priceDataError}
          >
            {priceData => (
              <Formik
                initialValues={{
                  numMessages: "",
                  messageType: "SMS",
                  segmentsPerMessage: "2",
                  estimatedReplyRate: "",
                }}
                onSubmit={() => {}}
              >
                {({ values, setFieldValue }) => {
                  const numMessages = parseInt(values.numMessages);
                  const segmentsPerMessage = parseInt(
                    values.segmentsPerMessage
                  );
                  const estimatedReplyRate = parseFloat(
                    values.estimatedReplyRate
                  );

                  const smsSegmentPriceInMillicents =
                    priceData.getTenantPriceData.currentPrice
                      .smsSegmentPriceInMillicents;
                  const mmsMessagePriceInMillicents =
                    priceData.getTenantPriceData.currentPrice
                      .mmsMessagePriceInMillicents;

                  let recommendedBudgetMillicents: number | null = null;
                  if (
                    values.messageType === "SMS" &&
                    !isNaN(numMessages) &&
                    !isNaN(segmentsPerMessage) &&
                    !isNaN(estimatedReplyRate)
                  ) {
                    recommendedBudgetMillicents =
                      numMessages *
                      segmentsPerMessage *
                      smsSegmentPriceInMillicents *
                      (1 + estimatedReplyRate / 100);
                  } else if (
                    values.messageType === "MMS" &&
                    !isNaN(numMessages) &&
                    !isNaN(estimatedReplyRate)
                  ) {
                    recommendedBudgetMillicents =
                      numMessages *
                      mmsMessagePriceInMillicents *
                      (1 + estimatedReplyRate / 100);
                  }

                  return (
                    <>
                      <InputRow>
                        <FormikTextField
                          name="numMessages"
                          label="Number of Messages"
                        />
                        <FormikSelect
                          name="messageType"
                          label="Message Type"
                          onChange={async event => {
                            if (event.target.value === "MMS") {
                              await setFieldValue("segments", "-");
                            }
                          }}
                        >
                          <MenuItem value="SMS">SMS</MenuItem>
                          <MenuItem value="MMS">MMS</MenuItem>
                        </FormikSelect>
                      </InputRow>
                      <InputRow>
                        <FormikTextField
                          name="segmentsPerMessage"
                          label="Segments Per Message"
                          disabled={values.messageType === "MMS"}
                          sx={{
                            "& .MuiInputBase-input.Mui-disabled": {
                              backgroundColor: palette.lightestGray,
                            },
                          }}
                        />
                        <FormikTextField
                          name="estimatedReplyRate"
                          label="Estimated Reply Rate"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ marginRight: 1 }}
                              >
                                %
                              </InputAdornment>
                            ),
                          }}
                        />
                      </InputRow>
                      <Divider />
                      <FlexBox justifyContent="flex-end">
                        <Typography variant="body1" sx={{ marginTop: 2 }}>
                          {recommendedBudgetMillicents === null ? (
                            "Fill in fields to see recommended budget"
                          ) : (
                            <>
                              Recommended Budget:{" "}
                              <strong>
                                $
                                {formatMillicentsAsDollars(
                                  recommendedBudgetMillicents
                                )}
                              </strong>
                            </>
                          )}
                        </Typography>
                      </FlexBox>
                    </>
                  );
                }}
              </Formik>
            )}
          </AsyncWrapper>
        </Box>
      </CollapsibleDrawer>
    </Box>
  );
}
